import { useState } from 'react';
import { clsx } from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import type { DraftSection, SearchTermData } from '../types';
import { sectionList, sectionRaw, sectionText } from '../utils/draftSectionText';
import { sanitizeReportText } from '../utils/reportText';
import {
  breadcrumb,
  parseChange,
  previewLimit,
  searchPreview,
  searchTermTake,
  valueLabel,
  type ChangePart,
  type SamTake,
} from '../utils/samTasks';
import { StepTitle } from './BettyReviewsReport';
import styles from './TaskPanels.module.css';

type ValuePart = Extract<ChangePart, { kind: 'value' }>;

/** Copies a value and says so for a moment. */
function useCopy(): [(key: string, value: string) => void, (key: string) => boolean] {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch {
      setCopied(null);
    }
  };
  return [copy, (key) => copied === key];
}

/**
 * Sam's search content, website fixes and Business Profile posts: his take with the search numbers
 * in plain words, the change as the owner will see it (a Google result preview, plain steps with the
 * code folded, or the post as it will look), each value with its own Copy, a checklist of what to
 * confirm, and why he suggests it folded away.
 */
export function SamProposalPanel({
  skillKey,
  sections,
  searchTerm,
  agentName,
}: {
  skillKey: string;
  sections: DraftSection[];
  searchTerm?: SearchTermData;
  agentName: string;
}) {
  const checks = sectionList(sections, 'checks');
  const why = sectionText(sections, 'why');
  const [ticked, setTicked] = useState<Record<number, boolean>>({});
  const [whyOpen, setWhyOpen] = useState(false);
  const [copy, isCopied] = useCopy();
  const isPost = skillKey === 'gbp_post';
  const isFix = skillKey === 'site_technical_fix';
  const change = isPost ? null : parseChange(sectionRaw(sections, 'change'));
  const preview = change && !isFix ? searchPreview(change.parts) : null;

  let take: SamTake | null = null;
  if (searchTerm) take = searchTermTake(searchTerm, isPost ? 'This post is about that search.' : '');
  const fallbackTake = isPost
    ? `This post is about "${sectionText(sections, 'searchTerm')}".${sectionText(sections, 'holiday') ? ` It ties in with ${sectionText(sections, 'holiday')}.` : ''}`
    : sanitizeReportText(change?.summary ?? '');
  const takeText = take?.sentences ?? fallbackTake;
  // When the summary is the take, the card starts after it rather than repeating it.
  const parts = change && !take && !isPost && change.summary ? withoutSummary(change.parts, change.summary) : change?.parts ?? [];
  let step = 0;
  const nextStep = () => (step += 1);

  return (
    <div className={styles.panelColumns}>
      <div className={styles.panelLayout}>
        <div className={styles.panelMain}>
          {takeText && (
          <section className={clsx(styles.take, styles.takeSam)} aria-label={`${agentName}'s take`}>
            <div className={clsx(styles.takeKicker, styles.takeKickerSam)}>{agentName}&apos;s take</div>
            <p className={styles.takeText}>{takeText}</p>
            {take && (
              <div className={styles.tilesThree}>
                {take.tiles.map((tile) => (
                  <div key={tile.label} className={clsx(styles.tile, styles.tileSam)}>
                    <div className={styles.tileLabel}>{tile.label}</div>
                    <div className={styles.tileValue}>{tile.value}</div>
                    <div className={styles.tileNote}>{tile.note}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
          )}

          {isPost && <PostPreview sections={sections} step={nextStep()} />}

          {preview && (
            <section className={styles.section}>
              <StepTitle step={nextStep()} title="How it would look on Google" sub={searchTerm?.pageUrl ? undefined : 'No page shows for this search yet.'} />
              <div className={styles.googleResult}>
                {breadcrumb(searchTerm?.pageUrl ?? pageOf(change!.parts)) && <div className={styles.googleUrl}>{breadcrumb(searchTerm?.pageUrl ?? pageOf(change!.parts))}</div>}
                <div className={styles.googleTitle}>{preview.title}</div>
                <div className={styles.googleDescription}>{preview.description}</div>
              </div>
              <div className={styles.hint}>A preview: Google decides the final look, and may shorten a long title or description.</div>
            </section>
          )}

          {change && parts.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <StepTitle
                  step={nextStep()}
                  title={isFix ? 'What your web person needs to do' : 'Text to copy'}
                  sub={isFix ? 'Code is folded for whoever makes the change.' : 'Each value goes into your website on its own.'}
                />
                <Button variant="secondary" size="sm" onClick={() => copy('all', sectionRaw(sections, 'change'))}>
                  {isCopied('all') ? 'Copied' : 'Copy for your web person'}
                </Button>
              </div>
              <div className={styles.card}>
                {parts.map((part, i) =>
                  part.kind === 'prose' ? (
                    <div key={i} className={styles.markdown}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sanitizeReportText(part.text)}</ReactMarkdown>
                    </div>
                  ) : (
                    <ValueRow key={i} part={part} label={valueLabel(part, preview)} folded={isFix} copied={isCopied(`v${i}`)} onCopy={() => copy(`v${i}`, part.value)} />
                  ),
                )}
              </div>
            </section>
          )}

        </div>

        <aside className={styles.panelRail} aria-label="Before you approve">
          {isPost && <PostLengths sections={sections} />}

          {checks.length > 0 && (
            <section className={styles.section}>
              <StepTitle
                step={nextStep()}
                title="Before you approve"
                sub={`${Object.values(ticked).filter(Boolean).length} of ${checks.length} checked. They're for you; ${agentName} doesn't see them.`}
              />
              <div className={styles.card}>
                {checks.map((item, i) => (
                  <label key={i} className={styles.checkItem}>
                    <input type="checkbox" className={styles.checkbox} checked={!!ticked[i]} onChange={() => setTicked((t) => ({ ...t, [i]: !t[i] }))} />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </section>
          )}

          {why && (
            <section className={styles.fold}>
              <button type="button" className={styles.foldHeader} aria-expanded={whyOpen} onClick={() => setWhyOpen((v) => !v)}>
                <span className={styles.foldTitle}>
                  Why {agentName} suggests this <span className={styles.optional}>Optional</span>
                </span>
                <ChevronDown size={18} aria-hidden="true" className={clsx(styles.chevron, whyOpen && styles.chevronOpen)} />
              </button>
              {whyOpen && (
                <div className={clsx(styles.foldBody, styles.markdown)}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{why}</ReactMarkdown>
                </div>
              )}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

/** The parts without the summary line that opens the first prose part. */
function withoutSummary(parts: ChangePart[], summary: string): ChangePart[] {
  const i = parts.findIndex((p) => p.kind === 'prose');
  const first = parts[i];
  if (!first || first.kind !== 'prose' || !first.text.startsWith(summary)) return parts;
  const rest = first.text.slice(summary.length).trim();
  return rest ? parts.map((p, j) => (j === i ? { kind: 'prose', text: rest } : p)) : parts.filter((_, j) => j !== i);
}

/** The page a change names in its `###` heading, when that is an address. */
function pageOf(parts: ChangePart[]): string | null {
  const page = parts.find((p): p is ValuePart => p.kind === 'value' && !!p.page)?.page ?? null;
  return page && /^https?:\/\//.test(page) ? page : null;
}

function ValueRow({ part, label, folded, copied, onCopy }: { part: ValuePart; label: string; folded: boolean; copied: boolean; onCopy: () => void }) {
  const [open, setOpen] = useState(!folded);
  const isCode = /^\s*[<{]/.test(part.value) || part.lang === 'json' || part.lang === 'html';
  const limit = previewLimit(label);
  const over = limit !== null && part.value.length > limit;
  return (
    <div className={styles.valueRow}>
      <div className={styles.valueHead}>
        <span className={styles.valueLabel}>{label}</span>
        <span className={styles.valueActions}>
          {folded && (
            <button type="button" className={styles.linkButton} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
              {open ? 'Hide code' : 'Show code'}
            </button>
          )}
          <Button variant="secondary" size="sm" onClick={onCopy}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </span>
      </div>
      {open && <div className={isCode ? styles.valueCode : styles.valueText}>{part.value}</div>}
      {open && limit !== null && (
        <div className={styles.meterRow}>
          <progress className={clsx(styles.meter, over && styles.meterOver)} max={limit} value={Math.min(part.value.length, limit)} aria-hidden="true" />
          <span className={over ? styles.meterTextOver : styles.meterText}>
            {part.value.length} characters{over ? `: Google may cut off the end past about ${limit}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}

/** A Business Profile post as it will look on Google. */
function PostPreview({ sections, step }: { sections: DraftSection[]; step: number }) {
  const headline = sectionRaw(sections, 'headline');
  const post = sectionRaw(sections, 'post');
  const button = sectionText(sections, 'button');
  const photo = sectionText(sections, 'photo');
  return (
    <section className={styles.section}>
      <StepTitle step={step} title="The post, as it will look on Google" sub="Posted to your Business Profile when you approve." />
      <div className={styles.postCard}>
        {photo && <div className={styles.postPhoto}>Photo to add: {photo}</div>}
        <div className={styles.postBody}>
          <div className={styles.postMeta}>Your Business Profile · when you approve</div>
          {headline && <div className={styles.postHeadline}>{headline}</div>}
          {post && <div className={styles.postText}>{post}</div>}
          {button && button.toLowerCase() !== 'none' && <span className={styles.postButton}>{button}</span>}
        </div>
      </div>
    </section>
  );
}

/** Each post field's length against Google's limit. */
function PostLengths({ sections }: { sections: DraftSection[] }) {
  const lengths = sections.filter((s) => (s.key === 'headline' || s.key === 'post') && s.maxChars);
  if (lengths.length === 0) return null;
  return (
    <section className={styles.postLengths} aria-label="Length">
      <div className={styles.tileLabel}>Length</div>
      {lengths.map((s) => {
        const value = Array.isArray(s.value) ? s.value.join('\n') : s.value;
        return (
          <div key={s.key} className={styles.postLength}>
            <div className={styles.postLengthHead}>
              <span>{s.label}</span>
              <span className={styles.meterText}>
                {value.length} of {s.maxChars}
              </span>
            </div>
            <progress className={styles.meter} max={s.maxChars} value={Math.min(value.length, s.maxChars!)} aria-hidden="true" />
          </div>
        );
      })}
    </section>
  );
}
